import * as api from "./client";

// ============================================================================
// Type Definitions — the DEV-2235 contract seam (freeze these exactly).
// ============================================================================
//
// The timestamp/actor fields are camelCase (`createdAt`/`createdBy`/
// `deletedAt`/`deletedBy`) because that is exactly what the backend returns.
// The list envelope is `{ data, meta }` with `total`/`limit`/`offset` (NOT
// `page`), mirroring userManagement.service.ts.

/** A blocked email domain row as returned by the backend. */
export type BlockedEmailDomain = {
  id: string;
  domain: string;
  reason: string | null;
  createdAt: string;
  createdBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
};

/** A profanity allow-list term row — same shape, `term` instead of `domain`. */
export type ProfanityAllowlistTerm = {
  id: string;
  term: string;
  reason: string | null;
  createdAt: string;
  createdBy: string | null;
  deletedAt: string | null;
  deletedBy: string | null;
};

/** Standard paginated list envelope for the registration-gate endpoints. */
export type ListEnvelope<T> = {
  data: T[];
  meta: { total: number; limit: number; offset: number };
};

/** Query params accepted by the list endpoints. */
export type ListParams = {
  search?: string;
  limit?: number;
  offset?: number;
};

const BLOCKED_DOMAINS_PATH = "/v1/blocked-email-domains";
const ALLOWLIST_PATH = "/v1/profanity-allowlist";

function buildQueryString(params: ListParams | undefined): string {
  const searchParams = new URLSearchParams();
  if (params?.search !== undefined && params.search !== "") {
    searchParams.append("search", params.search);
  }
  if (params?.limit !== undefined) {
    searchParams.append("limit", String(params.limit));
  }
  if (params?.offset !== undefined) {
    searchParams.append("offset", String(params.offset));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

function deletePath(basePath: string, id: string, reason?: string): string {
  const suffix = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return `${basePath}/${id}${suffix}`;
}

export const registrationGateListsService = {
  // ---- Blocked email domains -------------------------------------------------

  listBlockedDomains: async (params?: ListParams): Promise<ListEnvelope<BlockedEmailDomain>> => {
    return api.get<ListEnvelope<BlockedEmailDomain>>(
      `${BLOCKED_DOMAINS_PATH}${buildQueryString(params)}`,
    );
  },

  addBlockedDomain: async (input: {
    domain: string;
    reason?: string;
  }): Promise<BlockedEmailDomain> => {
    const response = await api.post<{ data: BlockedEmailDomain }>(BLOCKED_DOMAINS_PATH, input);
    return response.data;
  },

  // reason is a QUERY param, not a body — `del` has no body param.
  removeBlockedDomain: async (id: string, reason?: string): Promise<void> => {
    await api.del(deletePath(BLOCKED_DOMAINS_PATH, id, reason));
  },

  // ---- Profanity allow-list --------------------------------------------------

  listAllowlistTerms: async (
    params?: ListParams,
  ): Promise<ListEnvelope<ProfanityAllowlistTerm>> => {
    return api.get<ListEnvelope<ProfanityAllowlistTerm>>(
      `${ALLOWLIST_PATH}${buildQueryString(params)}`,
    );
  },

  addAllowlistTerm: async (input: {
    term: string;
    reason?: string;
  }): Promise<ProfanityAllowlistTerm> => {
    const response = await api.post<{ data: ProfanityAllowlistTerm }>(ALLOWLIST_PATH, input);
    return response.data;
  },

  // reason is a QUERY param, not a body — `del` has no body param.
  removeAllowlistTerm: async (id: string, reason?: string): Promise<void> => {
    await api.del(deletePath(ALLOWLIST_PATH, id, reason));
  },
};
